import { usePaymentTimeline } from '@/api/use-payment-timeline';
import { cn } from '@/utils/cn';
import { getIsoStatusMessage } from '@/utils/get-iso-status-message';
import { PaymentStatus } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';

const MIN_PROCESSING_DISPLAY_MS = 2500;

type SuccessStepProps = {
  recipientName: string;
  amount: string;
  reference: string;
  transactionId: string | null;
  onDone: () => void;
};

type StatusConfigParams = {
  status: PaymentStatus | undefined;
  isLoading: boolean;
  hasError: boolean;
  isoStatusCode?: string | null;
};

const getStatusConfig = ({ status, isLoading, hasError, isoStatusCode }: StatusConfigParams) => {
  if (hasError) {
    return {
      icon: <AlertTriangle size={30} className="text-destructive" />,
      title: 'Unable to load payment status',
      description: 'We could not retrieve your payment status. Your payment may still be processing.',
      borderClass: 'border-destructive',
    };
  }

  if (isLoading || !status) {
    return {
      icon: <Loader2 size={30} className="animate-spin text-muted-foreground" />,
      title: 'Processing payment...',
      description: 'Please wait while we process your payment.',
      borderClass: 'border-muted-foreground',
    };
  }

  switch (status) {
    case PaymentStatus.SUCCEEDED:
      return {
        icon: <Check size={30} />,
        title: 'Transfer successful',
        description: 'Your payment has been completed successfully.',
        borderClass: 'border-primary',
      };
    case PaymentStatus.FAILED: {
      const isoMessage = getIsoStatusMessage(isoStatusCode ?? null);
      return {
        icon: <X size={30} className="text-destructive" />,
        title: 'Transfer failed',
        description: isoMessage ?? 'Please contact your bank for more information.',
        borderClass: 'border-destructive',
      };
    }
    case PaymentStatus.PROCESSING:
      return {
        icon: <Check size={30} className="text-black" strokeWidth={1} />,
        title: 'Transfer submitted',
        description:
          "We're confirming the transfer with your bank. This usually takes a couple of minutes but can take a few hours in some cases.",
        borderClass: 'border-primary',
      };
    case PaymentStatus.CREATED:
    default:
      return {
        icon: <Loader2 size={30} className="animate-spin text-muted-foreground" />,
        title: 'Processing payment...',
        description: 'Please wait while we process your payment.',
        borderClass: 'border-muted-foreground',
      };
  }
};

/**
 * Custom hook to ensure PROCESSING state is shown for a minimum duration before showing final status.
 * Always shows PROCESSING first, then after MIN_PROCESSING_DISPLAY_MS shows the actual status.
 */
const useMinimumProcessingDisplay = (actualStatus: PaymentStatus | undefined) => {
  const [isMinTimeElapsed, setIsMinTimeElapsed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMinTimeElapsed(true);
    }, MIN_PROCESSING_DISPLAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // Always show PROCESSING until minimum time has passed
  if (!isMinTimeElapsed) {
    return PaymentStatus.PROCESSING;
  }

  // After minimum time, show actual status (or PROCESSING if still loading)
  return actualStatus ?? PaymentStatus.PROCESSING;
};

export const SuccessStep = ({ recipientName, amount, reference, transactionId, onDone }: SuccessStepProps) => {
  const { isPaymentTimelineEnabled } = useFlags<{ isPaymentTimelineEnabled: boolean }>();
  const { timeline, error } = usePaymentTimeline(isPaymentTimelineEnabled ? transactionId : null);

  // When feature flag is disabled or there's no transactionId (e.g., non-SCA payments), we can't poll for status.
  // Treat as PROCESSING ("Your transfer is on the way") since the payment
  // has already been submitted successfully to reach this step.
  // Note: SCA-declined payments never reach this step - they show an error toast and stay on PAYMENT_FORM.
  const displayStatus = useMinimumProcessingDisplay(
    isPaymentTimelineEnabled && transactionId ? timeline?.currentStatus : PaymentStatus.PROCESSING,
  );

  const statusConfig = getStatusConfig({
    status: displayStatus,
    isLoading: false,
    hasError: !!error && !!transactionId && isPaymentTimelineEnabled,
    isoStatusCode: timeline?.isoStatusCode,
  });

  return (
    <Stack align="stretch" space={16} className="w-full rounded-lg bg-white p-8">
      <Stack space={3} align="center">
        <Inline
          justify="center"
          className={cn('h-[60px] w-[60px] rounded-full border bg-white p-3', statusConfig.borderClass)}
        >
          {statusConfig.icon}
        </Inline>

        <Stack space={2} align="center">
          <p className="text-xl font-medium text-black">{statusConfig.title}</p>

          <p className="text-center text-base leading-6 font-medium tracking-tight text-muted-foreground">
            {statusConfig.description}
          </p>
        </Stack>
      </Stack>

      <Stack space={6} align="stretch">
        <Stack space={3} align="stretch">
          <Inline justify="between">
            <p className="text-base font-medium text-muted-foreground">Recipient name</p>

            <p className="text-base font-medium text-black">{recipientName}</p>
          </Inline>

          <Inline justify="between">
            <p className="text-base font-medium text-muted-foreground">Amount</p>

            <p className="text-base font-medium text-black">{amount}</p>
          </Inline>

          {!!reference && (
            <Inline justify="between">
              <p className="text-base font-medium text-muted-foreground">Reference</p>

              <p className="text-base font-medium text-black">{reference}</p>
            </Inline>
          )}

          {isPaymentTimelineEnabled && timeline?.institutionName && (
            <Inline justify="between">
              <p className="text-base font-medium text-muted-foreground">Bank</p>

              <p className="text-base font-medium text-black">{timeline.institutionName}</p>
            </Inline>
          )}
        </Stack>

        <Stack space={3} align="stretch">
          <Button variant="default" onClick={onDone}>
            Done
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
